import { adSizeMappings, adSlotSizes, adTargetings } from "@config/ads";
import { AdTargetingType, AdUnitProps } from "@typings/Ads.d";
import { useCallback, useEffect, useState } from "react";

import { generateAdvertElementId } from "./helpers";

/**
 * This component renders an IMU ad.
 * @param rootClassName - The root class name.
 * @param type - The type of ad unit.
 * @param slot - The ad slot.
 * @param id - The id of the ad.
 * @see https://developers.google.com/doubleclick-gpt/reference
 * @returns A IMU ad component. If the script is not ready, it returns error. If the script is ready, it returns the ad.
 */
export const AdUnit = ({
  rootClassName = "",
  type,
  slot = "",
  id = generateAdvertElementId(type),
  pubAdTargeting,
  gsChannels,
  articleSlotTarget,
  innerAdUnitClass,
}: AdUnitProps) => {
  const [error, setError] = useState<string>("");
  const [adSlot, setAdSlot] =
    useState<ReturnType<typeof googletag.defineSlot>>();
  const adSlotSize = adSlotSizes[type] as googletag.GeneralSize | undefined;
  const adSizeMapping = adSizeMappings[type];
  const adTarget = adTargetings[type];

  /**
   * This function is called when the Google script is loaded.
   * It defines the ad slot and loads the ad.
   * @returns void
   * @see https://developers.google.com/doubleclick-gpt/reference#googletag.cmd.push
   */
  const handleDisplayAd = useCallback(() => {
    try {
      // Make sure that googletag.cmd exists.
      window.googletag =
        (window.googletag as typeof googletag | undefined) || {};
      ((window.googletag as typeof googletag).cmd as googletag.CommandArray) =
        ((window.googletag as typeof googletag).cmd as
          | googletag.CommandArray
          | undefined) || [];

      //Changes to update loaded dfp tags if the ad is not available
      const trimmedId = id.replace("dfp-ad-", "");
      const trimmedType = trimmedId.split("-")[0];
      const size = Object.keys(adSlotSizes[trimmedType]).map((_, index) => {
        const sizes = adSlotSizes[trimmedType][index];

        return sizes.toString().replaceAll(",", "x");
      });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const loaded_dfp_tags = window.loaded_dfp_tags
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          JSON.parse(window.loaded_dfp_tags)
        : {};
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (loaded_dfp_tags && !loaded_dfp_tags[trimmedId]) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        loaded_dfp_tags[trimmedId] = {
          adunit: slot,
          slotname: id,
          size: size.join(","),
        };
        const loadDfpTagsScriptId = "loadDfpTagsScript";
        const loadDfpTagsScript = document.getElementById(loadDfpTagsScriptId);
        if (loadDfpTagsScript) {
          loadDfpTagsScript.innerHTML = `var loaded_dfp_tags = '${JSON.stringify(
            loaded_dfp_tags,
          )}';`;
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          window.loaded_dfp_tags = JSON.stringify(loaded_dfp_tags);
        } else {
          const loadDfpTagsScript = document.createElement("script");

          loadDfpTagsScript.id = loadDfpTagsScriptId;
          loadDfpTagsScript.dataset.testid = loadDfpTagsScriptId;
          loadDfpTagsScript.innerHTML = `var loaded_dfp_tags = '${JSON.stringify(
            loaded_dfp_tags,
          )}';`;
          loadDfpTagsScript.type = "text/javascript";
          document.body.appendChild(loadDfpTagsScript);
        }
      }

      // Correct: Queueing the callback on the command queue.
      (
        (window.googletag as typeof googletag).cmd as googletag.CommandArray
      ).push(function () {
        const pageTargeting: Record<string, string | string[]> = {};
        pubAdTargeting?.forEach((target: AdTargetingType) => {
          pageTargeting[target.key] = target.value;
        });

        if (Object.keys(pageTargeting).length > 0) {
          googletag.setConfig({ targeting: pageTargeting });
        }

        // Remove any existing ad slots with the same id.
        googletag
          .pubads()
          .getSlots()
          .forEach(function (_slot: googletag.Slot) {
            if (_slot.getSlotElementId()) {
              if (_slot.getSlotElementId() === id) {
                googletag.destroySlots([_slot]);
              }
            }
          });
        // Create the ad slot.
        if (undefined != adSlotSize) {
          const _adSlot = googletag.defineSlot(
            slot,
            adSlotSize as googletag.GeneralSize,
            id,
          ) as googletag.Slot | undefined;

          if (undefined != _adSlot) {
            _adSlot.addService(googletag.pubads());

            if (adSizeMapping) _adSlot.defineSizeMapping(adSizeMapping);

            const slotTargeting: Record<string, string | string[]> = {};
            adTarget?.forEach((target: AdTargetingType) => {
              slotTargeting[target.key] = target.value;
            });

            // Set gs_channels targeting.
            if (gsChannels) {
              slotTargeting.gs_channels = gsChannels.join(",");
            }

            // Article slot targeting.
            articleSlotTarget?.forEach((target) => {
              slotTargeting[target.key] = target.value;
            });

            if (Object.keys(slotTargeting).length > 0) {
              _adSlot.setConfig({ targeting: slotTargeting });
            }

            googletag.setConfig({ singleRequest: true });
            googletag.enableServices();

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            window.googletag.slots[type] = _adSlot;

            // Store the ad slot for later use.
            setAdSlot(_adSlot);
          }
        }
      });
    } catch (_error: unknown) {
      setError("Please refresh");
    }
  }, [
    slot,
    id,
    adSlotSize,
    adSizeMapping,
    pubAdTargeting,
    adTarget,
    type,
    gsChannels,
    articleSlotTarget,
  ]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (slot && !adSlot) {
        handleDisplayAd();
      }
    }
  }, [slot, handleDisplayAd, adSlot]);

  useEffect(() => {
    /**
     * This function destroys the ad slot when the component is unmounted.
     * @returns void
     * @see https://developers.google.com/doubleclick-gpt/reference#googletag.destroySlots
     */
    return () => {
      const gTag = window.googletag as typeof googletag | undefined;
      if (gTag && gTag.apiReady && adSlot) {
        googletag.cmd.push(function () {
          googletag.destroySlots([adSlot]);
        });
      }
    };
  }, [adSlot]);

  return (
    <div className={rootClassName}>
      {!error ? (
        <div data-testid={id} id={id} className={innerAdUnitClass}></div>
      ) : (
        error
      )}
    </div>
  );
};
